import { Button, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateNumbersSelectValues } from '../../../hooks/useCreateNumbersSelectValues';
import {
  TeacherConstraints,
  teacherConstraintFields,
  teacherConstraintLabels,
} from '../../../hooks/useTeacherConstraints';
import styles from '../Constraints.module.scss';
import { ConstraintPriorities } from '../types';
import { NumberPriorityConstraintItem } from './FormItems/NumberPriorityConstraintItem';

type TeacherConditionsFormProps = {
  constraints: TeacherConstraints;
  onSubmit: (blob: TeacherConstraints) => Promise<boolean>;
  onClose: () => void;
};

type FormValues = Record<string, number | ConstraintPriorities | null>;

export const TeacherConditionsForm = ({ constraints, onSubmit, onClose }: TeacherConditionsFormProps) => {
  const { t } = useTranslation();
  const labels = teacherConstraintLabels(t);
  const numberOptions = useCreateNumbersSelectValues();

  const ranges = useMemo(
    () => ({
      posPerDay: numberOptions.filter((o) => (o.value as number) >= 1),
      perWeek: numberOptions.filter((o) => (o.value as number) >= 1 && (o.value as number) <= 7),
      fromZero: numberOptions,
    }),
    [numberOptions],
  );

  const initialValues: FormValues = useMemo(() => {
    const values: FormValues = {};
    for (const descriptor of teacherConstraintFields) {
      values[descriptor.field] = (constraints[descriptor.field] as number | undefined) ?? null;
      if (descriptor.priorityField) {
        values[descriptor.priorityField] =
          (constraints[descriptor.priorityField] as ConstraintPriorities | undefined) ?? 'OPTIMIZING';
      }
    }
    return values;
  }, [constraints]);

  // Which constraints are currently shown — seeded from the ones the teacher already has.
  const [selected, setSelected] = useState<string[]>([]);
  useEffect(() => {
    setSelected(teacherConstraintFields.filter((d) => constraints[d.field] != null).map((d) => d.field));
  }, [constraints]);

  const addOptions: SelectOptionType[] = teacherConstraintFields
    .filter((d) => !selected.includes(d.field))
    .map((d) => ({ value: d.field, label: labels[d.field] }));

  const handleSubmit = async (values: FormValues) => {
    const blob: Record<string, number | ConstraintPriorities> = {};
    for (const descriptor of teacherConstraintFields) {
      if (!selected.includes(descriptor.field)) continue;
      const value = values[descriptor.field];
      if (typeof value !== 'number') continue;
      blob[descriptor.field] = value;
      if (descriptor.priorityField) {
        blob[descriptor.priorityField] = (values[descriptor.priorityField] as ConstraintPriorities) ?? 'OPTIMIZING';
      }
    }
    const success = await onSubmit(blob as TeacherConstraints);
    if (success) onClose();
  };

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {({ dirty, isSubmitting, resetForm, setFieldValue, setFieldTouched }) => {
        const removeField = (field: string) => {
          const descriptor = teacherConstraintFields.find((d) => d.field === field);
          setSelected(selected.filter((s) => s !== field));
          setFieldValue(field, null);
          setFieldTouched(field, true);
          if (descriptor?.priorityField) setFieldValue(descriptor.priorityField, 'OPTIMIZING');
        };
        const shown = teacherConstraintFields.filter((d) => selected.includes(d.field));
        return (
          <Form className={styles['conditions-form']}>
            <Select
              isTrigger
              className={styles['master-select']}
              placeholder={t('conditions.pleaseSelect')}
              options={addOptions}
              name='addCondition'
              menuPosition='fixed'
              onChange={(value) => {
                const field = (value as SelectOptionType).value as string;
                setSelected([field, ...selected]);
              }}
              label={t('conditions.add')}
            />
            <div className='tks__grid no-gap'>
              {shown.map((descriptor) => (
                <NumberPriorityConstraintItem
                  key={descriptor.field}
                  label={labels[descriptor.field]}
                  numberName={descriptor.field}
                  priorityName={descriptor.priorityField}
                  numberOptions={ranges[descriptor.range]}
                  onRemove={() => removeField(descriptor.field)}
                />
              ))}
            </div>
            <div className='modal-bottom'>
              <Button
                hierarchy='tertiary'
                type='button'
                onClick={() => {
                  resetForm();
                  onClose();
                }}
              >
                {t('common.cancelChanges')}
              </Button>
              <Button type='submit' disabled={isSubmitting || !dirty}>
                {t('common.save')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
